import styled, { css } from "styled-components";

interface ItemSubStyleProps {
  $shade?: boolean;
}

export const ExerciseStateBox = styled.div`
  display: flex;
  width: calc(100vw - 32px);
  flex-direction: column;
  gap: 10px;
`;

export const ExerciseStateTit = styled.div`
  color: ${(props) => props.theme.colors.black80};
  font-size: 1.3rem;
  font-weight: 400;
`;

export const ItemBox = styled.div`
  display: flex;
  padding: 24px;
  background: ${(props) => props.theme.colors.white100};
  border-radius: 10px;
  box-shadow: ${(props) => props.theme.shadows.box};
  flex-direction: column;
  gap: 24px;
`;

export const ItemHeader = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 7px;
`;

export const HeaderTit = styled.div`
  display: flex;
  width: 100%;
  color: ${(props) => props.theme.colors.text5};
  font-size: 1.6rem;
  font-weight: 600;
  align-items: center;
  justify-content: space-between;
`;

export const PartCount = styled.div`
  display: flex;
  padding: 6px 8px;
  color: ${(props) => props.theme.colors.white100};
  font-size: 1.1rem;
  font-weight: 500;
  background: ${(props) => props.theme.colors.black80};
  border-radius: 4px;
  align-items: center;
  justify-content: center;
  gap: 4px;

  svg {
    width: 14px;
    height: 14px;
  }
`;

export const HeaderSubTxt = styled.div`
  display: flex;
  color: ${(props) => props.theme.colors.text2};
  font-size: 1.2rem;
  font-weight: 300;
  align-items: center;
`;

export const ItemSubBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

export const ItemSub = styled.div<ItemSubStyleProps>`
  display: flex;
  padding: ${(props) => (props.$shade === true ? "14px 16px" : "0px 4px")};
  border-radius: ${(props) => (props.$shade === true ? "16px" : "0")};
  background: ${(props) =>
    props.$shade === true ? props.theme.colors.mainBg : "none"};
  border-radius: ${(props) => (props.$shade === true ? "8px" : "0")};
  align-items: center;
  justify-content: space-between;

  ${HeaderTit} {
    font-size: 1.5rem;
  }
`;

export const ItemSubHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

export const ItemSubTit = styled.div`
  display: flex;
  color: ${(props) => props.theme.colors.black80};
  font-size: 1.4rem;
  font-weight: 600;
  align-items: center;
  gap: 4px;

  span {
    color: ${(props) => props.theme.colors.text2};
    font-size: 1.1rem;
  }
`;

export const DayExerciseComplete = styled.div<{
  $type: "complete" | "incomplete";
}>`
  display: flex;
  color: ${(props) =>
    props.$type === "complete"
      ? props.theme.colors.primary100
      : props.theme.colors.text1};
  font-size: 1.4rem;
  font-weight: ${(props) => (props.$type === "complete" ? 600 : 300)};
  align-items: center;
  justify-content: center;
  gap: 5px;

  ${(props) =>
    props.$type === "complete" &&
    css`
      svg {
        path {
          fill: ${props.theme.colors.primary100};
        }
      }
    `}

  ${(props) =>
    props.$type === "incomplete" &&
    css`
      svg {
        circle {
          fill: ${props.theme.colors.white100};
        }
      }
    `}
`;

export const ParticipationContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const Participation = styled.div`
  display: grid;
  padding-bottom: 30px;
  width: 100%;
  border-bottom: 1px solid ${(props) => props.theme.colors.black5};
  grid-template-columns: repeat(6, 1fr);
  grid-template-rows: repeat(2, 1fr);
  gap: 4px;
`;

interface Props {
  isFilled: boolean;
}

export const CumulativeParticipationBox = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "isFilled",
})<Props>`
  display: flex;
  width: 100%;
  padding: 8px 0;
  color: ${(props) => props.theme.colors.white100};
  background: ${(props) =>
    props.isFilled ? props.theme.colors.primary100 : props.theme.colors.black5};
  border-radius: 6px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 6px;

  & > span {
    color: ${(props) =>
      props.isFilled ? props.theme.colors.white100 : props.theme.colors.text3};
    font-size: 0.9rem;
    font-weight: 600;
  }

  & > div {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    gap: 2px;

    span {
      color: ${(props) =>
        props.isFilled
          ? props.theme.colors.white100
          : props.theme.colors.text1};
      font-size: 0.8rem;
    }

    ${(props) =>
      props.isFilled === false &&
      css`
        svg {
          circle {
            stroke: ${props.theme.colors.white100};
            fill: ${props.theme.colors.white100};
          }
        }
      `}

    ${(props) =>
      props.isFilled === true &&
      css`
        svg {
          circle {
            stroke: ${(props) => props.theme.colors.white100};
          }

          path {
            fill: ${(props) => props.theme.colors.white100};
          }
        }
      `}
  }
`;

export const ItemCompleteCircle = styled.div`
  display: flex;
  color: ${(props) => props.theme.colors.primary100};
  font-size: 1.4rem;
  font-weight: 600;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  gap: 5px;

  svg {
    path {
      fill: ${(props) => props.theme.colors.primary100};
    }
  }
`;

export const ItemNotCompleteCircle = styled.div`
  display: flex;
  color: ${(props) => props.theme.colors.text1};
  font-size: 1.4rem;
  font-weight: 300;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  gap: 5px;
`;
